import cx from 'classnames';
import { useMemo } from 'react';

import { Note as NoteType } from 'data/features/notesTypes';

import Note from './components/Note';
import Styles from './components/Note.module.scss';

type NotesListProps = {
  compact?: boolean;
  emptyLabel?: string;
  isOrganizerScreen?: boolean;
  notes: NoteType[];
  phaseId: string;
  showDimensionName?: boolean;
  teamId: string;
  unredacted?: boolean;
};

/**
 * Returns the dimension header for a note.
 * If the note is the first of its dimension, it returns the dimension name.
 * Otherwise, it returns null.
 */
const getDimensionHeader = (note: NoteType, displayedDimensions: Set<string>): string | null => {
  const dimensionName = note.dimensionName || 'Overall';
  const isFirst = !displayedDimensions.has(dimensionName);
  displayedDimensions.add(dimensionName);
  return isFirst ? dimensionName : null;
};

const NotesList = ({
  compact,
  emptyLabel = 'None.',
  isOrganizerScreen,
  notes,
  phaseId,
  showDimensionName,
  teamId,
  unredacted,
}: NotesListProps) => {
  const firstInternalNote = notes.findIndex((note) => note.type === 'internal');
  const displayedDimensions = new Set<string>();

  let prevDimensionName = '';
  let currentGroupId = '';

  const groupedNotes = useMemo(() => {
    return notes.length
      ? notes.reduce(
          (acc, note) => {
            const dimensionName = note.dimensionName || 'Overall';
            if (dimensionName !== prevDimensionName) {
              currentGroupId = String(note.dimensionId);
              prevDimensionName = dimensionName;
            }

            acc[currentGroupId] ??= [];
            acc[currentGroupId].push(note);

            return acc;
          },
          {} as Record<string, NoteType[]>,
        )
      : {};
  }, [notes]);

  if (!notes?.length) return <small>{emptyLabel}</small>;

  return Object.keys(groupedNotes).map((groupId) => {
    const notes = groupedNotes[groupId] || [];
    const dimensionHeader = getDimensionHeader(notes[0], displayedDimensions);

    return (
      <div className={cx(Styles.Group, 'rounded-3')} key={groupId}>
        {dimensionHeader && <h2 className="d-none d-print-block">{dimensionHeader}:</h2>}
        {notes.map((note, index) => (
          <Note
            key={note.id}
            note={note}
            isLast={index === notes.length - 1}
            isFirstInternal={index === firstInternalNote && note.type === 'internal'}
            isOrganizerScreen={isOrganizerScreen}
            phaseId={phaseId}
            teamId={teamId}
            compact={compact}
            showDimensionName={showDimensionName}
            unredacted={unredacted}
          />
        ))}
      </div>
    );
  });
};

export default NotesList;
